import { z } from "zod";
import type { ChatMessage } from "./messages.js";

const THREADS_KEY = "mairko-matrix-chat-threads";
const PLACE_KEY = "mairko-matrix-place";
const MAX_PERSISTED_MESSAGES = 80;

const threadSchema = z.object({
	id: z.string(),
	title: z.string(),
	updatedAt: z.string(),
	messages: z.array(z.unknown()).optional().default([]),
	pinned: z.boolean().optional(),
	titleLocked: z.boolean().optional(),
	titleGenerated: z.boolean().optional(),
});

export type MairkoThread = {
	id: string;
	title: string;
	updatedAt: string;
	messages: ChatMessage[];
	/** Pinned chats sort above Recents. */
	pinned?: boolean;
	/** When true, title is user-set and not overwritten. */
	titleLocked?: boolean;
	/** When true, LLM named the chat — keep it unless the user renames. */
	titleGenerated?: boolean;
};

function titleFromMessages(messages: ChatMessage[]): string {
	const firstHuman = messages.find(
		(m) => m.author === "human" && m.body.trim().length > 0,
	);
	if (!firstHuman) return "New chat";
	const line = firstHuman.body.trim().replace(/\s+/g, " ");
	return line.length > 42 ? `${line.slice(0, 42)}…` : line;
}

export function createMairkoThread(): MairkoThread {
	const now = new Date().toISOString();
	return {
		id: crypto.randomUUID(),
		title: "New chat",
		updatedAt: now,
		/** Empty until first send — home greeting shows instead of a welcome bubble. */
		messages: [],
	};
}

export type ChatPlace = {
	activeThreadId: string;
	sitePreviewUrl: string | null;
	chatSurface:
		| "messages"
		| "agents"
		| "connectors"
		| "channels"
		| "sites";
};

const placeSchema = z.object({
	activeThreadId: z.string(),
	sitePreviewUrl: z.string().nullable(),
	chatSurface: z.enum([
		"messages",
		"agents",
		"connectors",
		"channels",
		"sites",
	]),
});

export function readChatPlace(): ChatPlace | null {
	try {
		const raw = localStorage.getItem(PLACE_KEY);
		if (!raw) return null;
		const parsed = placeSchema.safeParse(JSON.parse(raw) as unknown);
		return parsed.success ? parsed.data : null;
	} catch {
		return null;
	}
}

export function writeChatPlace(place: ChatPlace): void {
	try {
		localStorage.setItem(PLACE_KEY, JSON.stringify(place));
	} catch {
		/* ignore */
	}
}

export function threadHasResumeWork(thread: MairkoThread): boolean {
	return thread.messages.some(
		(row) =>
			row.author === "human" ||
			(row.companyResearch?.length ?? 0) > 0 ||
			(row.siteClones?.length ?? 0) > 0 ||
			(row.siteDesigns?.length ?? 0) > 0 ||
			Boolean(row.workingCopyAsk),
	);
}

/** Auto-injected tenant dump — not a real reply in that chat. */
export function isInjectedSiteCard(message: ChatMessage): boolean {
	if (message.author !== "agent") return false;
	if (!message.siteClones?.length) return false;
	return (
		message.body === "Your site preview is ready — open it anytime." ||
		message.body === "Here’s the latest on your site clone."
	);
}

/** Past the working-copy paywall — same chat continues in the full desk. */
export function threadUnlocksDesk(thread: MairkoThread): boolean {
	return thread.messages.some(
		(row) =>
			row.workingCopyAsk?.decision === "yes" ||
			(row.siteClones?.length ?? 0) > 0,
	);
}

function isPersistedMessage(value: unknown): value is ChatMessage {
	if (!value || typeof value !== "object") return false;
	const row = value as Record<string, unknown>;
	return (
		typeof row.id === "string" &&
		typeof row.agentId === "string" &&
		typeof row.authorName === "string" &&
		typeof row.body === "string" &&
		typeof row.at === "string" &&
		(row.author === "agent" ||
			row.author === "human" ||
			row.author === "system")
	);
}

function persistableMessages(messages: ChatMessage[]): ChatMessage[] {
	return messages.slice(-MAX_PERSISTED_MESSAGES).map((message) => ({
		...message,
		pending: undefined,
		attachments: message.attachments?.filter(
			(att) =>
				typeof att.url === "string" &&
				!att.url.startsWith("blob:") &&
				!att.url.startsWith("data:"),
		),
	}));
}

/**
 * Restore the last desk (transcript + cards). DeskChatAgent history is a
 * text backup when this cache is empty.
 */
export function readMairkoThreads(): MairkoThread[] {
	try {
		const raw = localStorage.getItem(THREADS_KEY);
		if (!raw) {
			const seed = [createMairkoThread()];
			writeMairkoThreads(seed);
			return seed;
		}
		const parsed: unknown = JSON.parse(raw);
		if (!Array.isArray(parsed) || parsed.length === 0) {
			const seed = [createMairkoThread()];
			writeMairkoThreads(seed);
			return seed;
		}
		const threads: MairkoThread[] = [];
		for (const entry of parsed) {
			const row = threadSchema.safeParse(entry);
			if (!row.success) continue;
			threads.push({
				id: row.data.id,
				title: row.data.title || "New chat",
				updatedAt: row.data.updatedAt,
				messages: row.data.messages
					.filter(isPersistedMessage)
					.filter((row) => !isInjectedSiteCard(row)),
				pinned: row.data.pinned,
				titleLocked: row.data.titleLocked,
				titleGenerated: row.data.titleGenerated,
			});
		}
		if (threads.length === 0) {
			const seed = [createMairkoThread()];
			writeMairkoThreads(seed);
			return seed;
		}
		return threads;
	} catch {
		const seed = [createMairkoThread()];
		writeMairkoThreads(seed);
		return seed;
	}
}

/** Drop persisted Recents when server onboarding is not complete. */
export function resetMairkoThreads(): MairkoThread[] {
	try {
		localStorage.removeItem(PLACE_KEY);
	} catch {
		/* ignore */
	}
	const seed = [createMairkoThread()];
	writeMairkoThreads(seed);
	return seed;
}

export function writeMairkoThreads(threads: MairkoThread[]): void {
	try {
		const slim = threads.map((thread) => ({
			id: thread.id,
			title: thread.title,
			updatedAt: thread.updatedAt,
			messages: persistableMessages(thread.messages),
			pinned: thread.pinned,
			titleLocked: thread.titleLocked,
			titleGenerated: thread.titleGenerated,
		}));
		localStorage.setItem(THREADS_KEY, JSON.stringify(slim));
	} catch {
		/* ignore */
	}
}

export function upsertMairkoThread(
	threads: MairkoThread[],
	next: MairkoThread,
): MairkoThread[] {
	const existing = threads.find((t) => t.id === next.id);
	const titleLocked = next.titleLocked ?? existing?.titleLocked;
	const titleGenerated = next.titleGenerated ?? existing?.titleGenerated;
	const pinned = next.pinned ?? existing?.pinned;
	const keepNamedTitle =
		Boolean(titleLocked) || Boolean(titleGenerated);
	const titled: MairkoThread = {
		...next,
		pinned,
		titleLocked,
		titleGenerated,
		title: keepNamedTitle
			? next.title.trim() ||
				existing?.title ||
				titleFromMessages(next.messages)
			: titleFromMessages(next.messages),
		updatedAt: new Date().toISOString(),
	};
	const without = threads.filter((t) => t.id !== titled.id);
	return [titled, ...without];
}

/** Apply an LLM Recents title once — no-ops if the user already renamed. */
export function applyGeneratedMairkoTitle(
	threads: MairkoThread[],
	id: string,
	title: string,
): MairkoThread[] {
	const next = title.trim().replace(/\s+/g, " ");
	if (!next) return threads;
	const clipped = next.length > 56 ? `${next.slice(0, 56).trimEnd()}…` : next;
	return threads.map((thread) => {
		if (thread.id !== id) return thread;
		if (thread.titleLocked) return thread;
		return {
			...thread,
			title: clipped,
			titleGenerated: true,
			updatedAt: new Date().toISOString(),
		};
	});
}

/** Pinned first, then most recently updated. */
export function sortMairkoThreads(threads: MairkoThread[]): MairkoThread[] {
	return [...threads].sort((a, b) => {
		const pin = Number(Boolean(b.pinned)) - Number(Boolean(a.pinned));
		if (pin !== 0) return pin;
		return (
			new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
		);
	});
}

export function renameMairkoThread(
	threads: MairkoThread[],
	id: string,
	title: string,
): MairkoThread[] {
	const next = title.trim();
	if (!next) return threads;
	return threads.map((thread) =>
		thread.id === id
			? {
					...thread,
					title: next.length > 64 ? `${next.slice(0, 64)}…` : next,
					titleLocked: true,
					updatedAt: new Date().toISOString(),
				}
			: thread,
	);
}

export function togglePinMairkoThread(
	threads: MairkoThread[],
	id: string,
): MairkoThread[] {
	return threads.map((thread) =>
		thread.id === id
			? {
					...thread,
					pinned: !thread.pinned,
					updatedAt: new Date().toISOString(),
				}
			: thread,
	);
}

export function deleteMairkoThread(
	threads: MairkoThread[],
	id: string,
): { threads: MairkoThread[]; nextActiveId: string | null } {
	const remaining = threads.filter((t) => t.id !== id);
	if (remaining.length === 0) {
		const seed = createMairkoThread();
		return { threads: [seed], nextActiveId: seed.id };
	}
	return { threads: remaining, nextActiveId: remaining[0]?.id ?? null };
}
